'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { getUserType, isLoggedIn } from '@src/v3/utils/localStorage';
import { usePathname, useRouter } from 'next/navigation';
import {
  AGENT_MOBILE_ROUTES,
  APP_ROUTE,
  MOBILE_NAVBAR_ROUTES,
  RENTER_MOBILE_ROUTES,
} from '@src/v3/constants/routes';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import Loader from '../loader';
import useLoader from '@src/v3/hooks/useLoader';
import Link from 'next/link';
import { useUnreadMessages } from '@src/v3/contexts/unreadMessagesContext';
import clsx from 'clsx';
import LoginModal from '../loginModal';
import RegisterModal from '../registerModal';
import { useMobileNav } from '@src/v3/contexts/mobileNavContext';
import useProfile from '@src/v3/hooks/useProfile';
import { cn } from '@src/v3/lib/utils';

const MobileNav = () => {
  const { push } = useRouter();
  const pathname = usePathname();
  const { isLoading } = useLoader();
  const { isMobileNavVisible } = useMobileNav();
  const { isUserLoggedIn } = useProfile();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] =
    useState<boolean>(false);
  const [menus, setMenus] = useState(MOBILE_NAVBAR_ROUTES);
  const { hasUnreadCount } = useUnreadMessages();

  // Get navbar menus based on user type
  const getMenus = useCallback(() => {
    if (isLoggedIn() && getUserType() === ZEME_USERS_ROLE.RENTER)
      return setMenus(RENTER_MOBILE_ROUTES);
    else if (isLoggedIn() && getUserType() === ZEME_USERS_ROLE.AGENT)
      return setMenus(AGENT_MOBILE_ROUTES);
    else setMenus(MOBILE_NAVBAR_ROUTES);
    return false;
  }, []);

  useEffect(() => {
    getMenus();
  }, [getMenus, isUserLoggedIn, pathname]);
  if (!isMobileNavVisible) {
    return null;
  }

  return (
    <>
      <nav
        aria-label="Bottom navigation"
        aria-hidden="true"
        className="block sm:hidden border-t border-gray-300 fixed bottom-0 left-0 right-0 h-16 bg-white z-[70]"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <ul
          className={cn(
            menus?.length ? `grid-cols-${menus?.length}` : 'grid-cols-5',
            'px-2 grid items-center h-16'
          )}
        >
          {menus.map(
            (
              { label = '', value = '', icon, isVisibleBadge = false },
              index
            ) => {
              const Icon = icon;
              // For non-logged-in users, the center menu (index 2) should open login modal
              // For logged-in users, it should navigate to the route (Applications for renters, CRM for agents/landlords)
              if (index === 2 && !isLoggedIn()) {
                return (
                  <li key={index}>
                    <span
                      onClick={() => setIsLoginModalOpen(true)}
                      className={clsx(
                        'flex flex-col gap-y-2 items-center text-[10px] transition-colors',
                        pathname === value ? 'text-focus' : 'text-gray-700'
                      )}
                    >
                      <Icon />
                      <span>{label}</span>
                    </span>
                  </li>
                );
              }
              // Special handling for Profile icon in mobile navigation
              if (label === 'Profile') {
                if (!isLoggedIn()) {
                  return (
                    <li key={index}>
                      <span
                        onClick={() => setIsLoginModalOpen(true)}
                        className={clsx(
                          'flex flex-col gap-y-2 items-center text-[10px] transition-colors',
                          pathname === value ? 'text-focus' : 'text-gray-700'
                        )}
                      >
                        <Icon />
                        <span>{label}</span>
                      </span>
                    </li>
                  );
                } else {
                  return (
                    <li key={index}>
                      <Link
                        href={APP_ROUTE.SETTINGS}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          push(APP_ROUTE.SETTINGS);
                        }}
                        className={clsx(
                          'flex flex-col gap-y-2 items-center text-[10px] transition-colors',
                          // Highlight Profile icon when on any settings route
                          pathname === APP_ROUTE.SETTINGS ||
                            pathname === APP_ROUTE.EDIT_PROFILE_SETTINGS ||
                            pathname === APP_ROUTE.GENERAL_SETTINGS
                            ? 'text-focus'
                            : 'text-gray-700'
                        )}
                      >
                        <Icon />
                        <span>{label}</span>
                      </Link>
                    </li>
                  );
                }
              }

              // Default handling for other menu items
              return (
                <li key={index}>
                  <Link
                    href={value}
                    type="button"
                    onClick={() => push(value)}
                    className={clsx(
                      'flex flex-col gap-y-2 items-center text-[10px] transition-colors',
                      pathname === value ? 'text-focus' : 'text-gray-700'
                    )}
                  >
                    <div className="relative">
                      <Icon />
                      {isVisibleBadge && hasUnreadCount && (
                        <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-focus border-[2.5px] border-white"></div>
                      )}
                    </div>
                    <span>{label}</span>
                  </Link>
                </li>
              );
            }
          )}
        </ul>
      </nav>
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onOpenRegisterModal={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(true);
        }}
      />
      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onOpenLoginModal={() => {
          setIsRegisterModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
      {isLoading ? <Loader isVisible={isLoading} /> : null}
    </>
  );
};

export default MobileNav;
